<template>
  <div>
    <!-- Breadcrumbs -->
    <nav aria-label="breadcrumb" class="mt-5 pt-5 ps-3">
      <ol class="breadcrumb mb-3">
        <li class="breadcrumb-item">
          <router-link to="/">Home</router-link>
        </li>
        <li class="breadcrumb-item active" aria-current="page">
          All Workspaces
        </li>
      </ol>
    </nav>

    <div class="container-fluid">
      <!-- Tabs and Action Header -->
      <div class="d-flex justify-content-between align-items-center mb-4">
        <!-- Tabs -->
        <ul class="nav nav-tabs" role="tablist">
          <li class="nav-item" role="presentation">
            <button
              class="nav-link"
              :class="{ active: activeTab === 'default' }"
              @click="setActiveTab('default')"
              type="button"
            >
              Default
            </button>
          </li>
          <li class="nav-item" role="presentation">
            <button
              class="nav-link"
              :class="{ active: activeTab === 'byusers' }"
              @click="setActiveTab('byusers')"
              type="button"
            >
              By Users
            </button>
          </li>
        </ul>

        <!-- Right Side Actions -->
        <div class="d-flex gap-2">
          <!-- Create Button - Only show for Default tab -->
          <button
            v-if="activeTab === 'default'"
            class="btn btn-primary" 
            :disabled="!canCreate('main')"
            @click="router.push('/createproject')"
          >
            <i class="fa fa-plus me-2"></i>
            Create Workspace
          </button>
          <button
            class="btn btn-outline-secondary"
            @click="loadWorkspaces"
            :disabled="isLoading"
          >
            <i class="fa fa-refresh me-1"></i>
            Refresh
          </button>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-12">
          <div class="card">
            <div
              class="card-header"
            >
              <div>
                <h5 class="mb-1">
                  {{ activeTab === 'default' ? 'Default Workspaces' : 'User Workspaces' }}
                </h5>
                <small class="text-muted">
                  {{ activeTab === 'default' ? 'List of official default workspaces' : 'Workspaces created by registered users' }}
                </small>
              </div>
            </div>

            <div class="card-body">
              <!-- Loading State -->
              <div v-if="isLoading" class="text-center py-4">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-2">Loading all workspaces...</p>
              </div>

              <!-- Error State -->
              <div v-else-if="error" class="alert alert-danger">
                <div class="d-flex align-items-center">
                  <i class="fa fa-exclamation-triangle me-2"></i>
                  <div class="grow">
                    <strong>Error:</strong> {{ error }}
                  </div>
                  <button
                    class="btn btn-sm btn-outline-danger"
                    @click="loadWorkspaces"
                  >
                    <i class="fa fa-refresh"></i> Retry
                  </button>
                </div>
              </div>

              <!-- Workspaces Grid -->
              <div v-else-if="workspaces.length > 0" class="row">
                <div
                  v-for="workspace in workspaces"
                  :key="workspace._id"
                  class="col-lg-4 col-md-6 col-sm-12 mb-4"
                >
                  <div class="card h-100 workspace-card border-0 shadow-sm">
                    <!-- Workspace Logo -->
                    <div class="card-img-top workspace-logo-container">
                      <img
                        v-if="workspace.logo"
                        :src="workspace.logo"
                        :alt="workspace.title"
                        class="workspace-logo"
                      />
                      <div
                        v-else
                        class="workspace-logo-placeholder d-flex align-items-center justify-content-center"
                      >
                        <i class="fa fa-briefcase fa-2x text-muted"></i>
                      </div>
                    </div>

                    <div class="card-body d-flex flex-column">
                      <!-- Workspace Title -->
                      <h6
                        class="card-title text-truncate"
                        :title="workspace.title"
                      >
                        {{ workspace.title }}
                        <span
                          v-if="workspace.is_ai_generated"
                          class="badge bg-primary ms-2"
                          title="AI Generated Workspace"
                        >
                          <i class="fa fa-robot me-1"></i>AI
                        </span>
                      </h6>

                      <!-- Created By Information -->
                      <div class="mb-2">
                        <small class="text-muted">Created by:</small>
                        <div class="d-flex align-items-center mt-1">
                          <img
                            v-if="workspace.created_by?.logo"
                            :src="workspace.created_by.logo"
                            :alt="workspace.created_by.username"
                            class="rounded-circle me-2"
                            style="width: 20px; height: 20px; object-fit: cover"
                          />
                          <i
                            v-else
                            class="fa fa-user-circle me-2 text-muted"
                          ></i>
                          <span class="small fw-medium">
                            {{
                              workspace.created_by?.username || "Unknown User"
                            }}
                          </span>
                          <router-link
                            v-if="workspace.created_by?._id"
                            :to="`/user-workspaces/${workspace.created_by._id}`"
                            class="btn btn-link btn-sm p-0 ms-2"
                            title="View user's workspaces"
                          >
                            <i class="fa fa-external-link-alt text-muted"></i>
                          </router-link>
                        </div>
                      </div>

                      <!-- Workspace ID -->
                      <div class="mb-2">
                        <small class="text-muted">ID:</small>
                        <code class="bg-light px-1 rounded">{{
                          workspace._id.slice(-8)
                        }}</code>
                      </div>

                      <!-- Workspace Idea/Description -->
                      <p class="card-text text-muted small grow">
                        <span v-if="workspace.idea && workspace.idea.trim()">
                          {{ workspace.idea }}
                        </span>
                        <span v-else class="fst-italic"
                          >No description provided</span
                        >
                      </p>

                      <!-- Workspace Stats -->
                      <div class="mt-auto">
                        <div class="row g-2 text-center">
                          <div class="col-4">
                            <div class="d-flex flex-column">
                              <span class="h6 mb-0 text-primary">{{
                                workspace.no_of_lanes
                              }}</span>
                              <small class="text-muted">Lanes</small>
                            </div>
                          </div>
                          <div class="col-4">
                            <div class="d-flex flex-column">
                              <span class="h6 mb-0 text-success">{{
                                workspace.no_of_modules
                              }}</span>
                              <small class="text-muted">Modules</small>
                            </div>
                          </div>
                          <div class="col-4">
                            <div class="d-flex flex-column">
                              <span class="h6 mb-0 text-info">{{
                                workspace.no_of_people
                              }}</span>
                              <small class="text-muted">People</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Workspace Footer with Actions -->
                    <div class="card-footer bg-transparent border-top-0 pt-0">
                      <div
                        class="d-flex justify-content-between align-items-center"
                      >
                        <div class="text-muted">
                          <small>
                            <i class="fa fa-calendar me-1"></i>
                            Created {{ formatTimeAgo(workspace.created_at) }}
                          </small>
                        </div>
                        <div>
                          <button
                            class="btn btn-outline-primary btn-sm"
                            :disabled="!canView('main')"
                            @click="viewWorkspaceDetails(workspace)"
                          >
                            <i class="fa fa-eye me-1"></i>
                            View
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Empty State -->
              <div v-else class="text-center py-5">
                <div class="mb-3">
                  <i class="fa fa-briefcase fa-3x text-muted"></i>
                </div>
                <h5 class="text-muted">No Workspaces Found</h5>
                <p class="text-muted">No workspaces have been created yet.</p>
                <div class="mt-3">
                  <button class="btn btn-primary" @click="loadWorkspaces()">
                    <i class="fa fa-refresh me-1"></i>
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            <!-- Summary Stats Footer -->
            <div
              v-if="workspaces.length > 0"
              class="card-footer bg-light border-bottom"
            >
              <div class="row text-center">
                <div class="col-md-2 col-6 mb-2 mb-md-0">
                  <div class="d-flex flex-column">
                    <span class="h5 mb-0 text-primary">{{
                      tablePagination.total
                    }}</span>
                    <small class="text-muted">Total Workspaces</small>
                  </div>
                </div>
                <div class="col-md-2 col-6 mb-2 mb-md-0">
                  <div class="d-flex flex-column">
                    <span class="h5 mb-0 text-success">{{ totalLanes }}</span>
                    <small class="text-muted">Total Lanes</small>
                  </div>
                </div>
                <div class="col-md-2 col-6 mb-2 mb-md-0">
                  <div class="d-flex flex-column">
                    <span class="h5 mb-0 text-warning">{{ totalModules }}</span>
                    <small class="text-muted">Total Modules</small>
                  </div>
                </div>
                <div class="col-md-2 col-6 mb-2 mb-md-0">
                  <div class="d-flex flex-column">
                    <span class="h5 mb-0 text-info">{{ totalPeople }}</span>
                    <small class="text-muted">Total People</small>
                  </div>
                </div>
                <div class="col-md-2 col-6 mb-2 mb-md-0">
                  <div class="d-flex flex-column">
                    <span class="h5 mb-0 text-secondary">{{
                      uniqueCreators
                    }}</span>
                    <small class="text-muted">Unique Creators</small>
                  </div>
                </div>
                <div class="col-md-2 col-6">
                  <div class="d-flex flex-column">
                    <span class="h5 mb-0 text-dark">{{
                      aiGeneratedCount
                    }}</span>
                    <small class="text-muted">AI Generated</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pagination Footer -->
            <div
              v-if="workspaces.length > 0"
              class="card-footer d-flex justify-content-end align-items-center gap-3"
            >
              <!-- Rows per page -->
              <div class="d-flex align-items-center gap-2">
                <span class="text-muted small">Rows per page:</span>
                <select
                  class="form-select form-select-sm rows-select"
                  v-model="tablePagination.per_page"
                  @change="changeRows"
                >
                  <option v-for="n in [6, 12, 24, 48, 96]" :key="n" :value="n">
                    {{ n }}
                  </option>
                </select>
              </div>

              <!-- Pagination Buttons -->
              <div class="pagination-mini">
                <!-- Previous -->
                <button
                  class="page-btn prev_btn"
                  :disabled="!tablePagination.has_prev || isLoading"
                  @click="handlePageChange(tablePagination.current_page - 1)"
                >
                  «
                </button>
                <!-- Numbered Pages -->
                <button
                  v-for="(p, index) in visiblePages"
                  :key="index"
                  class="page-btn"
                  :class="{
                    'page-active': p === tablePagination.current_page,
                    'disabled-dots': p === '...',
                  }"
                  :disabled="p === '...' || isLoading"
                  @click="
                    p !== '...' &&
                      typeof p === 'number' &&
                      handlePageChange(p)
                  "
                >
                  {{ p }}
                </button>

                <!-- Next -->
                <button
                  class="page-btn next_btn"
                  :disabled="!tablePagination.has_next || isLoading"
                  @click="handlePageChange(tablePagination.current_page + 1)"
                >
                  »
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Workspace Details Modal -->
    <div
      v-if="showModal && selectedWorkspace"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Workspace Details: {{ selectedWorkspace.title }}
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeModal"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="col-md-4">
                <img
                  v-if="selectedWorkspace.logo"
                  :src="selectedWorkspace.logo"
                  :alt="selectedWorkspace.title"
                  class="img-fluid rounded"
                />
                <div
                  v-else
                  class="bg-light rounded d-flex align-items-center justify-content-center"
                  style="height: 200px"
                >
                  <i class="fa fa-briefcase fa-3x text-muted"></i>
                </div>
              </div>
              <div class="col-md-8">
                <h6>Basic Information</h6>
                <table class="table table-borderless table-sm">
                  <tbody>
                    <tr>
                      <td><strong>ID:</strong></td>
                      <td>
                        <code>{{ selectedWorkspace._id }}</code>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Title:</strong></td>
                      <td>{{ selectedWorkspace.title }}</td>
                    </tr>
                    <tr>
                      <td><strong>Created By:</strong></td>
                      <td>
                        <div class="d-flex align-items-center">
                          <img
                            v-if="selectedWorkspace.created_by?.logo"
                            :src="selectedWorkspace.created_by.logo"
                            :alt="selectedWorkspace.created_by.username"
                            class="rounded-circle me-2"
                            style="width: 24px; height: 24px; object-fit: cover"
                          />
                          <i
                            v-else
                            class="fa fa-user-circle me-2 text-muted"
                          ></i>
                          <div class="d-flex flex-column">
                            <span>{{
                              selectedWorkspace.created_by?.username ||
                              "Unknown User"
                            }}</span>
                            <small class="text-muted">{{
                              selectedWorkspace.created_by?.email || ""
                            }}</small>
                          </div>
                          <router-link
                            v-if="selectedWorkspace.created_by?._id"
                            :to="`/user-workspaces/${selectedWorkspace.created_by._id}`"
                            class="btn btn-link btn-sm p-0 ms-2"
                            title="View user's workspaces"
                            @click="closeModal"
                          >
                            <i class="fa fa-external-link-alt"></i>
                          </router-link>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Idea:</strong></td>
                      <td>
                        {{
                          selectedWorkspace.idea || "No description provided"
                        }}
                      </td>
                    </tr>
                    <tr>
                      <td><strong>AI Generated:</strong></td>
                      <td>
                        <span
                          :class="
                            selectedWorkspace.is_ai_generated
                              ? 'badge bg-success'
                              : 'badge bg-secondary'
                          "
                        >
                          <i
                            :class="
                              selectedWorkspace.is_ai_generated
                                ? 'fa fa-robot'
                                : 'fa fa-user'
                            "
                            class="me-1"
                          ></i>
                          {{
                            selectedWorkspace.is_ai_generated
                              ? "Yes (AI Generated)"
                              : "No (Manual)"
                          }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <h6>Statistics</h6>
                <table class="table table-borderless table-sm">
                  <tbody>
                    <tr>
                      <td><strong>Lanes:</strong></td>
                      <td>
                        <span class="badge bg-primary">{{
                          selectedWorkspace.no_of_lanes
                        }}</span>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Modules:</strong></td>
                      <td>
                        <span class="badge bg-success">{{
                          selectedWorkspace.no_of_modules
                        }}</span>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>People:</strong></td>
                      <td>
                        <span class="badge bg-info">{{
                          selectedWorkspace.no_of_people
                        }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import apiService from "@/services/apiService";
import { usePermissions } from "@/composables/usePermissions";
const { canView, canCreate, canDelete, canUpdate } = usePermissions(); 

const router = useRouter();

// Types
interface User {
  _id: string;
  username: string;
  logo: string | null;
  email: string;
}

interface Workspace {
  _id: string;
  logo: string | null;
  title: string;
  idea?: string;
  no_of_lanes: number;
  no_of_modules: number;
  no_of_people: number;
  is_ai_generated: boolean;
  created_at: string;
  created_by: User;
}

// Reactive data
const workspaces = ref<Workspace[]>([]);
const filteredWorkspaces = ref<Workspace[]>([]);
const selectedWorkspace = ref<Workspace | null>(null);
const isLoading = ref(false);
const error = ref<string | null>(null);
const showModal = ref(false);

// Tab state
const activeTab = ref<"default" | "byusers">("default");

// Pagination state
const tablePagination = ref({
  current_page: 1,
  per_page: 12,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

// Computed properties
const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  if (!total || total <= 4) {
    return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  pages.push(1);

  if (current > 4) pages.push("...");

  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);

  if (current < total - 3) pages.push("...");

  pages.push(total);
  return pages;
});

// Computed properties
const totalLanes = computed(() =>
  workspaces.value.reduce((sum, ws) => sum + ws.no_of_lanes, 0)
);

const totalModules = computed(() =>
  workspaces.value.reduce((sum, ws) => sum + ws.no_of_modules, 0)
);

const totalPeople = computed(() =>
  workspaces.value.reduce((sum, ws) => sum + ws.no_of_people, 0)
);

const uniqueCreators = computed(() => {
  const creatorIds = new Set(
    workspaces.value.map((ws) => ws.created_by?._id).filter(Boolean)
  );
  return creatorIds.size;
});

const aiGeneratedCount = computed(
  () => workspaces.value.filter((ws) => ws.is_ai_generated).length
);

// Methods
const setActiveTab = (tab: "default" | "byusers") => {
  activeTab.value = tab;
  tablePagination.value.current_page = 1; // Reset to first page
  loadWorkspaces();
};

const changeRows = async () => {
  tablePagination.value.current_page = 1;
  await loadWorkspaces();
};

const handlePageChange = async (page: number) => {
  if (page < 1 || page === tablePagination.value.current_page) return;
  if (
    tablePagination.value.total_pages &&
    page > tablePagination.value.total_pages
  )
    return;

  tablePagination.value.current_page = page;
  await loadWorkspaces();
};

const loadWorkspaces = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    const page = tablePagination.value.current_page;
    const limit = tablePagination.value.per_page;
    const isDefault = activeTab.value === "default";

    console.log(`Loading ${activeTab.value} workspaces (page: ${page}, limit: ${limit})...`);
    
    // Passing pagination and tab parameters
    const response = await apiService.get("/common/cms/all-workspaces", {
      default: isDefault,
      page,
      limit,
    });

    console.log("Workspaces API Response:", response);

    // Standard format handle: { status, message, data, pagination }
    if (response && response.data && Array.isArray(response.data)) {
      workspaces.value = response.data;
      
      // Update pagination metadata
      if (response.pagination) {
        tablePagination.value.total = response.pagination.total || 0;
        tablePagination.value.total_pages = response.pagination.total_pages || 1;
        tablePagination.value.has_next = page < tablePagination.value.total_pages;
        tablePagination.value.has_prev = page > 1;
      }
      
      console.log(`Loaded ${workspaces.value.length} workspaces`);
    } else if (Array.isArray(response)) {
      // Fallback for old format if necessary
      workspaces.value = response;
      tablePagination.value.total = response.length;
      tablePagination.value.total_pages = 1;
      tablePagination.value.has_next = false;
      tablePagination.value.has_prev = false;
    } else {
      console.error("Invalid response structure:", response);
      throw new Error(`Invalid response format`);
    }
  } catch (err: any) {
    console.error("Error loading workspaces:", err);
    error.value = err?.response?.data?.message || err?.message || "Failed to load workspaces";
    workspaces.value = [];
  } finally {
    isLoading.value = false;
  }
};

const viewWorkspaceDetails = (workspace: Workspace) => {
  selectedWorkspace.value = workspace;
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  selectedWorkspace.value = null;
};

const formatTimeAgo = (createdAt: string): string => {
  const date = new Date(createdAt);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) {
    return "today";
  } else if (diffInDays === 1) {
    return "yesterday";
  } else if (diffInDays < 7) {
    return `${diffInDays} days ago`;
  } else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
  } else if (diffInDays < 365) {
    const months = Math.floor(diffInDays / 30);
    return `${months} month${months > 1 ? "s" : ""} ago`;
  } else {
    const years = Math.floor(diffInDays / 365);
    return `${years} year${years > 1 ? "s" : ""} ago`;
  }
};

// Lifecycle
onMounted(() => {
  loadWorkspaces();
});
</script>

<style scoped>
/* Tab styling */
.nav-tabs .nav-link {
  border-bottom: 2px solid transparent;
  color: #6c757d;
  font-weight: 500;
  padding: 0.5rem 1rem;
}

.nav-tabs .nav-link.active {
  background-color: transparent;
  border-color: transparent transparent #35bfbf transparent;
  color: #35bfbf;
  font-weight: 700;
}

.nav-tabs .nav-link:hover {
  border-color: transparent transparent #dee2e6 transparent;
  color: #495057;
}

.workspace-card {
  transition: all 0.3s ease;
  cursor: default;
}

.workspace-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1) !important;
}

.workspace-logo-container {
  height: 180px;
  overflow: hidden;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.workspace-logo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.workspace-logo-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.card-title {
  font-weight: 600;
  color: #2c3e50;
}

.breadcrumb-item a {
  text-decoration: none;
  color: #6c757d;
}

.breadcrumb-item a:hover {
  color: #495057;
}

.spinner-border {
  width: 2rem;
  height: 2rem;
}

code {
  font-size: 0.75rem;
}

@media (max-width: 768px) {
  .workspace-logo-container {
    height: 150px;
  }

  .card-footer .col-6 {
    margin-bottom: 0.5rem;
  }
}

.table-borderless td {
  padding: 0.25rem 0.5rem;
}

.modal-body .img-fluid {
  max-height: 250px;
  width: 100%;
  object-fit: cover;
}

.fw-medium {
  font-weight: 500;
}

/* Pagination Styles */
.rows-select {
  width: auto;
  min-width: 80px;
  padding: 4px 8px;
  font-size: 0.85rem;
}

.pagination-mini {
  display: flex;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #dee2e6;
}

.page-btn {
  border: none;
  background: white;
  font-size: 0.9rem;
  padding: 6px 12px;
  cursor: pointer;
  color: #6c757d;
  transition: 0.2s;
  border-right: 1px solid #dee2e6;
  outline: none;
}

.page-btn:last-child {
  border-right: none;
}

.page-btn:hover:not(:disabled) {
  background: #f8f9fa;
  color: #21c1d6;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-active {
  background: #35bfbf !important;
  color: #fff !important;
  font-weight: 600;
}

.disabled-dots {
  cursor: default;
  background: #fff !important;
}

.prev_btn,
.next_btn {
  font-weight: bold;
  color: #35bfbf;
}

</style>
